"use client";

import { Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { imageStyle } from "@/lib/embed-image";

export type FloatingButtonProps = {
  agentName: string;
  agentLabel: string;
  avatarUrl: string;
  /**
   * 右端の丸に出す画像。管理画面の「FABアイコン画像」。
   * 未設定なら電話のアイコンのまま
   */
  iconUrl?: string;
  /** false なら埋め込み先の --showcan-avatar / --showcan-launcher-icon を見ない。既定は見る */
  avatarOverridable?: boolean;
  iconOverridable?: boolean;
  isOpen: boolean;
  onClick: () => void;
};

export default function FloatingButton({
  agentName,
  agentLabel,
  avatarUrl,
  iconUrl,
  avatarOverridable = true,
  iconOverridable = true,
  isOpen,
  onClick,
}: FloatingButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "showcan-launcher fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-full bg-white py-2 pl-2 pr-5 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5",
        isOpen && "translate-y-4 opacity-0 pointer-events-none"
      )}
    >
      {/*
       * 画像は img ではなく背景で置く。埋め込み先が --showcan-avatar を指定すれば
       * CSSだけで差し替えられるようにするため（img の src はCSSから触れない）
       */}
      <div
        role="img"
        aria-label={agentName}
        className="showcan-avatar h-10 w-10 shrink-0 rounded-full bg-cover bg-center ring-2 ring-[var(--showcan-ink-30)]"
        style={imageStyle("--showcan-avatar", avatarUrl, avatarOverridable)}
      />
      <div className="flex flex-col items-start">
        <span className="showcan-agent-name text-sm font-semibold text-gray-800">
          {agentName}
        </span>
        <span className="showcan-agent-label text-xs text-[var(--showcan-ink)]">
          {agentLabel}
        </span>
      </div>
      {/*
       * 画像は電話アイコンの上に重ねる。画像が無ければ background-image が none になり、
       * 下の電話アイコンがそのまま見える。埋め込み先が --showcan-launcher-icon を
       * 指定したときも、アイコンを消す指示を別途書かずに差し替わる
       */}
      <div className="showcan-launcher-icon relative ml-1 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--showcan-ink)] text-white">
        <Phone className="h-4 w-4" />
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={imageStyle("--showcan-launcher-icon", iconUrl, iconOverridable)}
        />
      </div>
    </button>
  );
}
